/* Incoming-call sheet — appears when CallContext.phase === "incoming".

   Mimics the iOS lock-screen style: large caller avatar + name with two
   prominent action buttons (decline = red, accept = green). Plays a loud
   two-tone ringtone via the shared `ringer` helper (which handles iOS
   AudioContext unlocking + suspended-state resume). */
import { useEffect } from "react";
import { Phone, PhoneSlash } from "@phosphor-icons/react";
import { CALL_PHASE, useCall } from "../../context/CallContext";
import { playRing, stopRing } from "../../lib/ringer";
import { VOICE_CALLS_ENABLED } from "../../lib/featureFlags";

function Avatar({ name, size = 140 }) {
  const initial = (name || "؟").trim().charAt(0);
  return (
    <div
      className="rounded-full bg-gradient-to-br from-[#E6C463] to-[#B8860B] text-white font-bold flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.35)] ring-4 ring-white/10"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      aria-hidden="true"
    >
      {initial}
    </div>
  );
}

function useRingtone(active) {
  useEffect(() => {
    if (!active) {
      stopRing();
      return undefined;
    }
    playRing("incoming");
    return () => stopRing();
  }, [active]);
}

export default function IncomingCallSheet() {
  const { phase, peer, acceptIncoming, declineIncoming } = useCall();
  const active = phase === CALL_PHASE.INCOMING;
  useRingtone(active);

  if (!active) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex flex-col"
      dir="rtl"
      data-testid="incoming-call-sheet"
      style={{
        background:
          "radial-gradient(ellipse at top, #1a2940 0%, #0A192F 55%, #061226 100%)",
        color: "white",
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <div className="pt-8 text-center text-[13px] tracking-wide text-white/60">
        مكالمة واردة
      </div>

      <div className="flex-1 flex flex-col items-center justify-center gap-5 px-6">
        <div className="relative">
          <Avatar name={peer?.name} size={150} />
          {/* expanding rings */}
          <span
            className="absolute inset-0 rounded-full"
            aria-hidden
            style={{
              boxShadow: "0 0 0 0 rgba(184,134,11,0.6)",
              animation: "lex-ring 1.6s ease-out infinite",
            }}
          />
          <style>{`
            @keyframes lex-ring {
              0% { box-shadow: 0 0 0 0 rgba(184,134,11,0.55); }
              100% { box-shadow: 0 0 0 36px rgba(184,134,11,0); }
            }
          `}</style>
        </div>
        <div className="text-center">
          <div className="text-[24px] font-bold">{peer?.name || "متّصل مجهول"}</div>
          {peer?.role && (
            <div className="mt-1 text-[14px] text-white/70">
              {peer.role === "lawyer" ? "محامٍ" : peer.role === "client" ? "عميل" : ""}
            </div>
          )}
        </div>
      </div>

      <div className="pb-12 px-8 flex items-center justify-between">
        <button
          type="button"
          onClick={declineIncoming}
          data-testid="incoming-decline"
          aria-label="رفض"
          className="w-20 h-20 rounded-full bg-[#E0413B] flex items-center justify-center shadow-[0_10px_40px_rgba(224,65,59,0.55)] active:scale-95 transition"
        >
          <PhoneSlash size={32} weight="fill" color="white" />
        </button>

        <button
          type="button"
          onClick={acceptIncoming}
          data-testid="incoming-accept"
          aria-label="ردّ"
          className="w-20 h-20 rounded-full bg-[#22C55E] flex items-center justify-center shadow-[0_10px_40px_rgba(34,197,94,0.55)] active:scale-95 transition"
        >
          <Phone size={32} weight="fill" color="white" />
        </button>
      </div>
    </div>
  );
}
